import { useId, useState } from "react";
import { Button } from "../../../../components/Button";
import { TextInput } from "../../../../components/TextInput";
import { createItineraryRequest } from "../../../../services/itinerary";
import type { ItineraryRequest } from "../../../../types/itinerary";
import { getErrorMessage } from "../../../../utils/errorHandler";
import { inclusiveDateToStartIso } from "../../../../utils/calendarTime";
import "./Itinerary.css";

interface ItineraryRequestFormProps {
  /** Seeds the name from whatever the coach was searching for when they found nothing. */
  defaultName?: string;
  /** YYYY-MM-DD, when the caller already knows the dates. */
  defaultStartDate?: string;
  /** YYYY-MM-DD, INCLUSIVE last day. */
  defaultEndDate?: string;
  onCancel: () => void;
  /** The created request, straight from the server, so the caller confirms what was RECORDED. */
  onSubmitted: (request: ItineraryRequest) => void;
}

/**
 * "Ask RosterPilot to build this one" — the form a coordinator fills in when the shared catalog
 * has nothing that matches.
 *
 * <b>The tournament name is the only required field.</b> Programs commit to a tournament long
 * before its schedule is published, so demanding a first and last day turned "ask us to build
 * this" into a guess — and a guessed date in RosterPilot's queue is worse than no date, because
 * staff cannot tell it apart from a real one. Dates are asked for and passed through when known.
 *
 * The schedule link is optional but is the single most useful thing staff can be handed, so it is
 * a first-class field rather than buried in the notes. It is validated SERVER-side (absolute
 * http/https only), and that server message is surfaced verbatim rather than replaced with a
 * generic "couldn't save" — the coach needs to know it is the link that is wrong.
 */
export const ItineraryRequestForm = ({
  defaultName = "",
  defaultStartDate = "",
  defaultEndDate = "",
  onCancel,
  onSubmitted,
}: ItineraryRequestFormProps) => {
  const fieldId = useId();
  const [tournamentName, setTournamentName] = useState(defaultName);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [locationName, setLocationName] = useState("");
  const [scheduleUrl, setScheduleUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    const name = tournamentName.trim();
    if (!name) {
      setError("Give the tournament a name.");
      return;
    }
    // A supplied date has to be a real one. `inclusiveDateToStartIso` yields "" for anything it
    // cannot parse, and "" satisfies `startDateUtc?: string` — so without this a truthy-but-bad
    // value (from a seeded default, say; the date input itself makes it hard to type) would ship an
    // empty string and come back as a model-binding 400 rather than the sentence below.
    const startIso = startDate ? inclusiveDateToStartIso(startDate) : "";
    const endIso = endDate ? inclusiveDateToStartIso(endDate) : "";
    if ((startDate && !startIso) || (endDate && !endIso)) {
      setError("Enter the dates as a real calendar date, or leave them blank.");
      return;
    }

    // Otherwise dates are optional, so the only thing left to check is that a supplied pair is the
    // right way round. One end on its own is allowed: "it starts on the 5th, we don't know for how
    // long" is real information, and the server records it as such.
    if (startDate && endDate && endDate < startDate) {
      setError("The last day can't be before the first day.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const created = await createItineraryRequest({
        tournamentName: name,
        // Both bounds are day-grained UTC midnight; the request's end day is INCLUSIVE, unlike a
        // calendar event's exclusive all-day end. Omitted entirely when blank — sending an empty
        // string would be a parse error, and sending a placeholder date would be a lie.
        ...(startIso ? { startDateUtc: startIso } : {}),
        ...(endIso ? { endDateUtc: endIso } : {}),
        ...(locationName.trim() ? { locationName: locationName.trim() } : {}),
        ...(scheduleUrl.trim() ? { scheduleUrl: scheduleUrl.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      onSubmitted(created);
    } catch (err) {
      // getErrorMessage, not getSafeErrorMessage: the server's message for a bad schedule link
      // quotes the URL, and the HTML-entity escaping would render it as literal `&#x2F;` noise.
      // React escapes interpolated text on its own, so this is not an injection path.
      setError(getErrorMessage(err, "Couldn't send the request. Try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      className="itin-request"
      data-testid="itinerary-request-form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <h3 className="itin-request__title">Request an itinerary</h3>
      <p className="itin-request__blurb">
        Tell us which tournament you're going to and we'll build the itinerary for you. The name is
        all we need — add whatever else you already know.
      </p>

      <TextInput
        className="itin-field"
        label="Tournament name"
        id={`${fieldId}-name`}
        value={tournamentName}
        onChange={(e) => setTournamentName(e.target.value)}
        placeholder="e.g. PBR Fall Championship"
        maxLength={200}
        data-testid="itinerary-request-name"
      />

      <div className="itin-field-row">
        <TextInput
          className="itin-field"
          label={
            <>
              First day <span className="itin-field__optional">(optional)</span>
            </>
          }
          id={`${fieldId}-start`}
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          data-testid="itinerary-request-start"
        />
        <TextInput
          className="itin-field"
          label={
            <>
              Last day <span className="itin-field__optional">(optional)</span>
            </>
          }
          id={`${fieldId}-end`}
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          data-testid="itinerary-request-end"
        />
      </div>
      <p className="itin-field__hint">
        Leave the dates blank if the tournament hasn't published its schedule yet.
      </p>

      <TextInput
        className="itin-field"
        label={
          <>
            Location <span className="itin-field__optional">(optional)</span>
          </>
        }
        id={`${fieldId}-location`}
        value={locationName}
        onChange={(e) => setLocationName(e.target.value)}
        placeholder="e.g. LakePoint Sporting Community"
        maxLength={300}
        data-testid="itinerary-request-location"
      />

      <div className="itin-field">
        <TextInput
          label={
          <>
            Schedule link <span className="itin-field__optional">(optional)</span>
          </>
        }
          id={`${fieldId}-url`}
          type="url"
          value={scheduleUrl}
          onChange={(e) => setScheduleUrl(e.target.value)}
          placeholder="https://…/schedule"
          data-testid="itinerary-request-url"
        />
        <p className="itin-field__hint">
          A link to the tournament's public schedule page gets us started much faster.
        </p>
      </div>

      <div className="itin-field">
        <label className="itin-label" htmlFor={`${fieldId}-notes`}>
          Notes <span className="itin-field__optional">(optional)</span>
        </label>
        <textarea
          id={`${fieldId}-notes`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          maxLength={4000}
          placeholder="Anything we should know — which teams you're following, pool vs. bracket, and so on"
          data-testid="itinerary-request-notes"
        />
      </div>

      {error && (
        <div className="event-editor__error" role="alert" data-testid="itinerary-request-error">
          {error}
        </div>
      )}

      <div className="itin-request__actions">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={submitting}
          data-testid="itinerary-request-submit"
        >
          {submitting ? "Sending…" : "Send request"}
        </Button>
      </div>
    </form>
  );
};
